import { useMemo } from 'react';
import type {
  GetPlansQuery,
  PlanPeriod,
  PlanTier,
} from '@frameio/graphql-client/generated';
import { ENTERPRISE_PLAN } from '../constants';

type PlansFromQuery = NonNullable<
  NonNullable<GetPlansQuery['account']>['plans']
>;

export type PlanFromQuery = PlansFromQuery[number];

type PlansByTier = {
  [key in PlanTier]?: PlanFromQuery;
};

type PlansByPeriodThenTier = {
  [key in PlanPeriod]?: PlansByTier;
};

/**
 * Using this empty object will maintain this sort order in the UI.
 * If we add another tier, we'll need to update this.
 */

const emptyPlansByTier: PlansByTier = {
  FREE: undefined,
  PRO: undefined,
  TEAM: undefined,
  // @ts-ignore
  ENTERPRISE: ENTERPRISE_PLAN,
};

export function usePlansForSelectedPeriod(
  plans: PlansFromQuery,
  isYearlySelected = false
): Array<PlanFromQuery> {
  const plansForSelectedPeriod: Array<PlanFromQuery> = useMemo(() => {
    const plansByPeriodThenTier: PlansByPeriodThenTier = plans.reduce(
      (
        filteredPlans: PlansByPeriodThenTier,
        plan: PlanFromQuery
      ): PlansByPeriodThenTier => {
        if (!filteredPlans[plan.period]) {
          /* eslint-disable-next-line no-param-reassign */
          filteredPlans[plan.period] = { ...emptyPlansByTier };
        }

        // This will filter out duplicates, with the last of the tier being included.
        // Duplicates can occur if multiple plans that share a period and tier are
        // set to default in Support Tool.
        /* eslint-disable-next-line no-param-reassign */
        filteredPlans[plan.period]![plan.tier] = plan;

        return filteredPlans;
      },
      {}
    );

    const plansForPeriod: Array<PlanFromQuery> = Object.values(
      plansByPeriodThenTier[isYearlySelected ? 'YEARLY' : 'MONTHLY'] || []
    ).filter((plan) => !!plan);

    return plansForPeriod;
  }, [isYearlySelected, plans]);

  return plansForSelectedPeriod;
}
